import { ChangeDetectionStrategy, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Select, Store } from '@ngxs/store';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription, firstValueFrom } from 'rxjs';
import { AIService } from 'src/app/ai/ai.service';
import { FetchAllBudgets } from 'src/app/budget/budget.actions';
import { AddLogItem, GetLogItemsByObjectId, UpdateLogItem } from 'src/app/logbook/logbook.actions';
import ChildInvoiceLink from 'src/app/models/ChildInvoiceLink';
import { LinkedObjectsToFileWithId } from 'src/app/models/LinkedObjectsToFileWithId';
import LogItem from 'src/app/models/Logbook/LogItem';
import YukiDocument from 'src/app/models/yuki/YukiDocument';
import YukiLinkObject from 'src/app/models/yuki/YukiLinkObject';
import { YukiService } from 'src/app/yuki/yuki.service';

import { LinkFile } from '../../../models/LinkFile';
import { LinkedObjectToFile } from '../../../models/LinkedObjectToFile';
import { roundNumber } from '../../utils/number-utils';
import { AddChildLinkModalComponent } from '../add-child-link-modal/add-child-link-modal.component';
import { ChildInvoiceLinkDTO } from '../add-child-link-modal/add-child-link-modal.component';
import { AddSubLinkToLinkedObjectToFile } from '../global-files.actions';
import { DeleteLinkedObject, GetAllObjectsLinkedToFile, GetAllObjectsToLink, LinkFileToObject, UpdateLinkedObject } from '../global-files.actions';
import { GlobalFilesState } from '../global-files.state';

@Component({
    selector: 'app-link-object-to-file-modal',
    templateUrl: './link-object-to-file-modal.component.html',
    styleUrls: ['./link-object-to-file-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class LinkObjectToFileModalComponent implements OnInit, OnDestroy, OnChanges {
    private static readonly INCLUDE_FLEET_STORAGE_KEY_PREFIX_BY_COMPANY_UUID = 'yuki-include-fleet-company:';
    private static readonly INCLUDE_FLEET_STORAGE_KEY_PREFIX_BY_SUPPLIER_NAME = 'yuki-include-fleet-supplier:';

    @Select(GlobalFilesState.LinkedObjectsToFileWithId) linkedObjectsToFile$: Observable<LinkedObjectsToFileWithId>;
    @Select(GlobalFilesState.LinkFiles) linkFiles$: Observable<LinkFile[]>;

    @Input() yukiDocument: YukiDocument;

    linkedObjectsToFile: LinkedObjectsToFileWithId;
    linkedObjectsToFileDatasource: MatTableDataSource<LinkedObjectToFile> = new MatTableDataSource<LinkedObjectToFile>();
    linkFiles: LinkFile[] = [];
    showedLinkFiles: LinkFile[] = [];
    totalPriceForAddedFiles = 0;

    linkFileForm: UntypedFormGroup;
    isLoading = false;
    radio = 'project';
    amountExcl: number;
    totalPriceAboveInvoiceAmount: boolean;
    DESCRIPTION_MAX_LENGTH = 1000;
    currentAmountOfDescriptionCharacters: number;
    descriptionIsTooLong = false;
    linkedObjectsAreLoaded = false;

    addedFileObjects: MatTableDataSource<YukiLinkObject> = new MatTableDataSource<YukiLinkObject>();
    displayedColumns: string[] = ['fileName', 'description', 'linkedObject', 'sublinks', 'actions'];

    unsavedChanges = false;
    isUpdating = false;
    isSaving = false;
    isDeleting = false;
    today: Date = new Date();

    // === AI state ===
    aiBusy = false;
    customPromptActive = false;
    // Company logbook prompt (AI-classifier)
    companyLogbookPrompt: string | null = null;
    companyLogbookItem: LogItem | null = null;
    includeFleetInAiContext = false;

    private subscriptions = new Subscription();

    constructor(
        private store: Store,
        private fb: UntypedFormBuilder,
        public activeModal: NgbActiveModal,
        private toastr: ToastrService,
        private modalService: NgbModal,
        private logger: NGXLogger,
        private aiService: AIService,
        private yuki: YukiService,
    ) {}

    // ===== Lifecycle =====
    ngOnInit(): void {
        this.fetchData();
        this.createLinkFileForm();

        if (this.yukiDocument.type == 2) {
            this.linkFileForm.controls['type'].setValue('purchase');
        } else {
            this.linkFileForm.controls['type'].setValue('sale');
        }
        this.totalPriceAboveInvoiceAmount = this.amountExcl < this.calculateTotalPrice();

        this.store.dispatch(new FetchAllBudgets());
        this.refreshPromptBadge();

        // Load company logbook prompt if available
        this.loadCompanyLogbookPrompt();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    ngOnChanges(_: SimpleChanges) {
        this.linkedObjectsAreLoaded = false;
        this.createLinkFileForm();
        this.fetchLinkedObjects();
        this.refreshPromptBadge();
        this.loadCompanyLogbookPrompt();
    }

    onIncludeFleetContextChanged(): void {
        this.saveIncludeFleetPreferenceForCurrentSupplier(this.includeFleetInAiContext);
    }

    private async loadCompanyLogbookPrompt(): Promise<void> {
        try {
            let company = this.yukiDocument?.linkedContact ? (this.yukiDocument.linkedContact as any) : null;
            if (!company?.uuid && this.yukiDocument?.uuid) {
                const refreshed = await firstValueFrom(this.yuki.fetchDocumentById(this.yukiDocument.uuid));
                if (refreshed?.linkedContact) {
                    this.yukiDocument = refreshed;
                    company = refreshed.linkedContact as any;
                }
            }

            if (!company?.uuid) {
                this.companyLogbookPrompt = null;
                this.companyLogbookItem = null;
                this.includeFleetInAiContext = this.readIncludeFleetPreferenceForCurrentSupplier(null);
                return;
            }

            this.includeFleetInAiContext = this.readIncludeFleetPreferenceForCurrentSupplier(company.uuid);

            await firstValueFrom(this.store.dispatch(new GetLogItemsByObjectId(company.uuid)));
            const logsForCompany = this.store.selectSnapshot((state: any) => state.logbook?.logItems)?.find((li: any) => li.objectId === company.uuid);
            const found: LogItem | undefined = logsForCompany?.logItems?.find((li: LogItem) => (li?.category || '').toLowerCase() === 'ai-classifier');
            if (found) {
                this.companyLogbookPrompt = found.description || null;
                this.companyLogbookItem = found;
            } else {
                this.companyLogbookPrompt = null;
                this.companyLogbookItem = null;
            }
        } catch (e) {
            this.logger.debug('Failed to load company logbook prompt', e);
            this.companyLogbookPrompt = null;
            this.companyLogbookItem = null;
        }
    }

    async saveCompanyLogbookPrompt(): Promise<void> {
        try {
            const company = this.yukiDocument?.linkedContact ? (this.yukiDocument.linkedContact as any) : null;
            if (!company?.uuid) {
                this.toastr.error('No company linked to document.');
                return;
            }

            this.saveIncludeFleetPreferenceForCurrentSupplier(this.includeFleetInAiContext, company.uuid);

            const payload: LogItem = {
                category: 'AI-classifier',
                objectId: company.uuid,
                description: this.companyLogbookPrompt || '',
            };

            if (this.companyLogbookItem && this.companyLogbookItem.id) {
                await firstValueFrom(this.store.dispatch(new UpdateLogItem(this.companyLogbookItem.id, payload)));
                this.toastr.success('Company logbook prompt updated.');
            } else {
                await firstValueFrom(this.store.dispatch(new AddLogItem(payload)));
                this.toastr.success('Company logbook prompt saved.');
            }

            // reload
            await this.loadCompanyLogbookPrompt();
        } catch (e) {
            this.logger.error('Failed to save company logbook prompt', e);
            this.toastr.error('Failed saving logbook prompt.');
        }
    }

    /** Public reload for the UI button to force re-fetching the company prompt */
    async reloadCompanyLogbookPrompt(): Promise<void> {
        try {
            await this.loadCompanyLogbookPrompt();
            this.toastr.success('Company AI-classifier prompt reloaded.');
        } catch (e) {
            this.logger.debug('Failed reloading company prompt', e);
            this.toastr.error('Failed to reload company prompt.');
        }
    }

    // ===== UI helpers =====
    refreshPromptBadge() {
        this.customPromptActive = !!this.aiService.getCustomPrompt();
    }

    async onEditPromptClick() {
        const updated = await this.aiService.openPromptEditor(this.aiService.getCustomPrompt());
        if (updated !== null) {
            this.refreshPromptBadge();
            this.toastr.success(updated ? 'Custom AI prompt updated.' : 'Custom AI prompt cleared.');
        }
    }

    changeValue($event) {
        this.linkFileForm.controls['object'].reset();
        this.radio = $event.target.value;
        this.filterShownObjectsToLink();
    }

    calculateDifferenceBetweenTotalPriceAndInvoicePrice() {
        const amount = this.calculateTotalPrice();
        return this.amountExcl - amount;
    }

    calculateTotalPrice() {
        if (!this.linkedObjectsToFileDatasource) return 0;
        return this.linkedObjectsToFileDatasource.data.reduce((sum, obj) => sum + obj.price, 0);
    }

    fetchLinkedObjects() {
        this.logger.debug(`Fetching linked objects for file ${this.yukiDocument.uuid}`);
        this.store.dispatch(new GetAllObjectsLinkedToFile(this.yukiDocument.uuid));
        this.subscriptions.add(
            this.linkedObjectsToFile$.subscribe((linkedObjectsToFile) => {
                if (linkedObjectsToFile && linkedObjectsToFile.yukiId == this.yukiDocument.uuid) {
                    this.linkedObjectsToFile = linkedObjectsToFile;
                    this.linkedObjectsToFileDatasource.data = linkedObjectsToFile.linkedObjects;
                    this.linkedObjectsAreLoaded = true;
                }
            }),
        );
    }

    deleteLinkedObject(row: LinkedObjectToFile) {
        this.isLoading = true;
        this.isDeleting = true;
        this.subscriptions.add(
            this.store.dispatch(new DeleteLinkedObject(this.yukiDocument.uuid, row.objectId)).subscribe({
                next: () => {
                    this.toastr.success('Successfully deleted object from ' + this.yukiDocument.subject);
                    this.logger.debug('Successfully deleted object from ' + this.yukiDocument.subject);
                    this.isLoading = false;
                    this.isDeleting = false;
                    this.fetchLinkedObjects();
                },
                error: () => {
                    this.toastr.error('Failed to link file with current object');
                    this.logger.error('Failed to delete link due to request error.');
                    this.isLoading = false;
                    this.isDeleting = false;
                },
            }),
        );
    }

    addLinkFile($event) {
        this.linkFileForm.controls['object'].setValue($event);
        this.updateTotalForAddedFileTable();

        const selectedObjectType = (this.radio || '').toLowerCase();
        const shouldOpenContractBudgetAfterCreate = selectedObjectType === 'project';
        const selectedObjectId = this.linkFileForm.value.object?.uuid;

        const linkFileDTO: LinkFileDTO = {
            description: this.linkFileForm.value.description,
            price: this.calculateRemainingAmount(),
            objectId: this.linkFileForm.value.object.uuid,
            invoiceType: this.linkFileForm.value.type,
        };

        this.sendNewLinksToBackend([linkFileDTO], shouldOpenContractBudgetAfterCreate ? selectedObjectId : null);

        this.linkFileForm.controls['object'].reset();
        this.linkFileForm.controls['description'].reset();
        this.totalPriceForAddedFiles += linkFileDTO.price;
    }

    totalLinkedAmountIsOverAmountExcl(): boolean {
        return this.calculateTotalLinkedAmount() > roundNumber(this.yukiDocument.amountExcl);
    }

    amountUsedByLinks(childInvoiceLinks: ChildInvoiceLink[]) {
        if (!childInvoiceLinks) return 0;
        return childInvoiceLinks.reduce((total, cil) => total + cil.amount, 0);
    }

    normalizeObjectType(objectType?: string): string {
        const normalized = (objectType || '').toString().trim().toLowerCase();
        if (!normalized) return '';
        if (normalized.includes('overhead')) return 'overheadcategory';
        if (normalized.includes('budget')) return 'budget';
        if (normalized.includes('project')) return 'project';
        if (normalized.includes('employee')) return 'employee';
        return normalized;
    }

    isKnownObjectType(objectType?: string): boolean {
        const normalized = this.normalizeObjectType(objectType);
        return normalized === 'employee' || normalized === 'project' || normalized === 'budget' || normalized === 'overheadcategory';
    }

    onDescriptionKeyUp() {
        this.descriptionIsTooLong = false;
        this.currentAmountOfDescriptionCharacters = this.linkFileForm.controls.description.value.length;
        if (this.currentAmountOfDescriptionCharacters > this.DESCRIPTION_MAX_LENGTH) {
            this.descriptionIsTooLong = true;
        }
    }

    updateTotalForAddedFileTable() {
        this.totalPriceForAddedFiles = this.addedFileObjects.data.reduce((sum, f) => sum + f.amount, 0);
    }

    openAddChildLinkModal(link: LinkedObjectToFile) {
        const modalRef = this.modalService.open(AddChildLinkModalComponent, {
            windowClass: 'modal-prompt modal-prompt--min-content',
            animation: false,
        });
        modalRef.componentInstance.link = link;
        modalRef.componentInstance.file = this.yukiDocument;
    }

    openContractBudgetLinkModal(event: Event, link: LinkedObjectToFile): void {
        event.stopPropagation();
        this.openContractBudgetLinkModalForRow(link);
    }

    private openContractBudgetLinkModalForRow(link: LinkedObjectToFile): void {
        const modalRef = this.modalService.open(AddChildLinkModalComponent, {
            windowClass: 'modal-prompt modal-prompt--min-content',
            animation: false,
        });
        modalRef.componentInstance.link = link;
        modalRef.componentInstance.file = this.yukiDocument;
        modalRef.componentInstance.allowedTypes = ['contract', 'projectAccount', 'employee'];
        modalRef.componentInstance.defaultType = 'contract';
    }

    updateLinkAmount(row: LinkedObjectToFile, newPrice: number): void {
        const linkFileDTO: LinkFileDTO = { description: row.description, price: newPrice, objectId: row.objectId };
        this.updateLink(row, linkFileDTO);
    }

    updateLinkDescription(row: LinkedObjectToFile, newDescription: string): void {
        const linkFileDTO: LinkFileDTO = { description: newDescription, price: row.price, objectId: row.objectId };
        this.updateLink(row, linkFileDTO);
    }

    inputEvent() {
        this.unsavedChanges = true;
    }

    private createLinkFileForm() {
        this.amountExcl = (this.yukiDocument as any).amount ? (this.yukiDocument as any).amountExcl : 0;
        this.amountExcl = Math.round(this.amountExcl * 100) / 100;

        this.linkFileForm = this.fb.group({
            object: [null, Validators.required],
            amount: [`${this.amountExcl}`, Validators.required],
            radio: [this.radio, Validators.required],
            type: [null, Validators.required],
            description: '',
        });
    }

    private filterShownObjectsToLink() {
        this.showedLinkFiles = this.linkFiles.filter((linkFile) => (linkFile.type || '').toLowerCase() === this.radio.toLowerCase());
    }

    private fetchData() {
        this.subscriptions.add(
            this.linkFiles$.subscribe((linkFiles) => {
                if (!linkFiles) {
                    this.store.dispatch(new GetAllObjectsToLink());
                    return;
                }
                this.linkFiles = linkFiles;
                this.filterShownObjectsToLink();
            }),
        );
        this.fetchLinkedObjects();
    }

    private calculateTotalLinkedAmount(): number {
        const alreadyLinkedAmount: number =
            this.linkedObjectsToFile?.linkedObjects?.reduce((linkedAmount: number, value: LinkedObjectToFile) => linkedAmount + value.price, 0) ?? 0;
        const linkedAmount: number = this.addedFileObjects.data.reduce((sum: number, value: YukiLinkObject) => sum + value.amount, 0);
        return linkedAmount + alreadyLinkedAmount;
    }

    private calculateRemainingAmount(): number {
        return roundNumber(roundNumber(this.yukiDocument.amountExcl) - this.calculateTotalLinkedAmount());
    }

    private sendNewLinksToBackend(objectsToLink: LinkFileDTO[], openContractBudgetForObjectId?: string | null): void {
        this.isSaving = true;
        this.store.dispatch(new LinkFileToObject(this.yukiDocument.uuid, objectsToLink)).subscribe(
            async () => {
                this.toastr.success('Successfully linked file');
                this.logger.debug('Successfully linked file');
                this.isSaving = false;
                try {
                    await this.postLinkProcessing(objectsToLink);
                } catch (e) {
                    this.logger.debug('Post-link processing failed', e);
                }
                this.fetchLinkedObjects();
            },
            () => {
                this.isSaving = false;
                this.logger.error('Failed to link yuki document');
                this.toastr.error('Oops, something went wrong, Please try again later...');
            },
        );
    }

    /** After linking files to objects, if a project was linked on a sale invoice, try to create sublinks.

        if (openContractBudgetForObjectId) {
          setTimeout(() => {
            try {
              const linkedObjects = this.store.selectSnapshot(GlobalFilesState.LinkedObjectsToFileWithId)?.linkedObjects || [];
              const projectRow = linkedObjects.find((row: LinkedObjectToFile) => row?.objectId === openContractBudgetForObjectId);
              if (projectRow) {
                this.openContractBudgetLinkModalForRow(projectRow);
              } else {
                this.toastr.info('Project linked. Je kan nu onder "Already created links" het contract/budget koppelen.');
              }
            } catch (e) {
              this.logger.debug('Could not auto-open contract/budget linking modal', e);
            }
          }, 200);
        }
   *  Strategy: use AddChildLinkModalComponent timesheet prefill logic.
   */
    private async postLinkProcessing(objectsToLink: LinkFileDTO[]): Promise<void> {
        if (!objectsToLink || objectsToLink.length === 0 || !this.yukiDocument) return;

        // Only operate for sale invoices
        if (this.yukiDocument.folderId !== 2) return;

        // Load current linkFiles if not present
        if (!this.linkFiles || this.linkFiles.length === 0) {
            await firstValueFrom(this.linkFiles$);
        }

        for (const lf of objectsToLink) {
            try {
                const linked = this.linkFiles.find((l) => String(l.uuid) === String(lf.objectId));
                if (!linked) continue;
                if ((linked.type || '').toLowerCase() !== 'project') continue;
                // Open timesheet prefill modal (existing behavior)
                const modalRef = this.modalService.open(AddChildLinkModalComponent, {
                    windowClass: 'modal-prompt modal-prompt--min-content',
                    animation: false,
                });
                // fetch latest linkedObjects to obtain project link details
                await firstValueFrom(this.store.dispatch(new GetAllObjectsLinkedToFile(this.yukiDocument.uuid)));
                const latest = await firstValueFrom(this.linkedObjectsToFile$).catch(() => null);
                const projectLink = latest?.linkedObjects?.find((o: any) => o.objectId === lf.objectId);
                modalRef.componentInstance.link = projectLink;
                modalRef.componentInstance.file = this.yukiDocument;
                await modalRef.result
                    .then(async () => {
                        await this.store.dispatch(new GetAllObjectsLinkedToFile(this.yukiDocument.uuid));
                        this.toastr.success('Sublinks created from timesheet prefill.');
                    })
                    .catch(() => {});
            } catch (e) {
                this.logger.debug('Failed processing linked object for sublinks', e);
            }
        }
    }

    private updateLink(row: LinkedObjectToFile, linkFileDTO: LinkFileDTO) {
        this.isUpdating = true;
        this.store.dispatch(new UpdateLinkedObject(this.yukiDocument.uuid, row.objectId, linkFileDTO)).subscribe({
            next: () => {
                this.toastr.success('Successfully updated amount');
                this.logger.debug('Successfully updated amount');
                this.isUpdating = false;
                this.unsavedChanges = false;
                this.fetchLinkedObjects();
            },
            error: () => {
                this.logger.error('Failed to update amount');
                this.toastr.error('Oops, something went wrong, Please try again later...');
                this.isUpdating = false;
            },
        });
    }

    // =========================================================
    // ========== AI helpers: bestand ophalen via API ==========
    // =========================================================
    private mimeToKind(mime?: string | null): 'pdf' | 'image' | null {
        if (!mime) return null;
        const m = mime.toLowerCase();
        if (m.includes('application/pdf')) return 'pdf';
        if (m.startsWith('image/')) return 'image';
        return null;
    }

    private buildDataUrl(mime: string, base64: string): string {
        return `data:${mime};base64,${base64}`;
    }

    private async resolveAiFileViaApi(yukiId: string): Promise<{ kind: 'pdf' | 'image'; dataUrl: string } | null> {
        // Verwacht response zoals:
        // { fileAsBase64: string, contentType: "application/pdf"|"image/*", fileName: string }
        const yukiFile: any = await firstValueFrom(this.yuki.fetchFile(yukiId));

        if (!yukiFile) return null;

        // 1) Gebruik de exacte keys uit jouw API
        const base64: string | null = yukiFile.fileAsBase64 ?? null;
        const mime: string | null = yukiFile.contentType ?? null;

        if (base64 && mime) {
            const lower = mime.toLowerCase();
            const kind: 'pdf' | 'image' | null = lower.includes('application/pdf') ? 'pdf' : lower.startsWith('image/') ? 'image' : null;

            if (!kind) return null;

            // Sommige backends sturen base64 met newlines — verwijderen kan geen kwaad
            const compact = base64.replace(/\r?\n|\r/g, '');

            const dataUrl = `data:${mime};base64,${compact}`;
            return { kind, dataUrl };
        }

        // 2) Eventuele alternatieve velden (fallbacks – blijven bestaan voor compatibiliteit)
        const direct = yukiFile?.dataUrl || yukiFile?.fileDataUrl;
        if (typeof direct === 'string' && direct.startsWith('data:')) {
            const m = direct.split(';', 1)[0].replace('data:', '').toLowerCase();
            const kind = m.includes('application/pdf') ? 'pdf' : m.startsWith('image/') ? 'image' : null;
            if (kind) return { kind, dataUrl: direct };
        }

        const altMime: string | null = yukiFile?.mimeType || yukiFile?.contentType || yukiFile?.fileMimeType || null;
        const altB64: string | null = yukiFile?.base64 || yukiFile?.fileBase64 || yukiFile?.content || null;
        if (altMime && altB64) {
            const l = altMime.toLowerCase();
            const kind = l.includes('application/pdf') ? 'pdf' : l.startsWith('image/') ? 'image' : null;
            if (!kind) return null;
            const dataUrl = `data:${altMime};base64,${altB64.replace(/\r?\n|\r/g, '')}`;
            return { kind, dataUrl };
        }

        // 3) Als er enkel een URL zou zijn (nu blijkbaar niet het geval), kun je hier nog fetch→dataURL doen.
        const url: string | null = yukiFile?.url || yukiFile?.downloadUrl || yukiFile?.publicUrl || null;
        if (url) {
            const resp = await fetch(url, { credentials: 'include' });
            if (!resp.ok) throw new Error(`Fetch file failed: ${resp.status} ${resp.statusText}`);
            const blob = await resp.blob();
            const blobMime = blob.type || altMime || '';
            const k = blobMime.includes('application/pdf') ? 'pdf' : blobMime.startsWith('image/') ? 'image' : null;
            if (!k) return null;
            const dataUrl = await new Promise<string>((resolve, reject) => {
                const fr = new FileReader();
                fr.onloadend = () => resolve(String(fr.result));
                fr.onerror = reject;
                fr.readAsDataURL(blob);
            });
            return { kind: k, dataUrl };
        }

        return null;
    }

    // =========================================================
    // AI AUTOFILL: laat AI classificeren en MAAK de links meteen aan
    // =========================================================
    async runAiAutofill(): Promise<void> {
        try {
            this.aiBusy = true;
            this.saveIncludeFleetPreferenceForCurrentSupplier(this.includeFleetInAiContext);
            const result: any = await firstValueFrom(
                this.yuki.autoAllocateDocumentWithContext(this.yukiDocument.uuid, {
                    vatOnly: false,
                    linkOnly: false,
                    includeFleet: this.includeFleetInAiContext,
                    aiHint: (this.companyLogbookPrompt || '').trim() || null,
                }),
            );
            console.info('[Yuki AI] Volledige vraag (prompt):', result?.aiPrompt || '(leeg)');
            console.info('[Yuki AI] Volledig antwoord:', result?.aiResponse || '(leeg)');
            await firstValueFrom(this.store.dispatch(new GetAllObjectsLinkedToFile(this.yukiDocument.uuid)));
            this.fetchLinkedObjects();

            if (Array.isArray(result?.allocations) && result.allocations.length > 0) {
                this.toastr.success('AI links created.');
            } else {
                const reason = (result?.noMatchReason || '').trim();
                console.info('[Yuki AI] No links created in modal autofill', {
                    documentUuid: this.yukiDocument?.uuid,
                    reason: reason || 'No reason returned',
                });
                this.toastr.info(reason ? `AI could not create links: ${reason}` : 'AI could not create any links.');
            }
        } catch (err: any) {
            this.logger.error(err);
            this.toastr.error(err?.message || 'AI autofill failed.');
        } finally {
            this.aiBusy = false;
        }
    }

    private buildAiDescription(note?: string | null): string {
        const base = this.linkFileForm?.value?.description || this.yukiDocument?.subject || '';
        const aiTag = '[AI]';
        if (note && note.trim().length) {
            if (base && base.trim().length) return `${aiTag} ${base} — ${note}`;
            return `${aiTag} ${note}`;
        }
        if (base && base.trim().length) return `${aiTag} ${base}`;
        return aiTag;
    }

    get isAiRunning(): boolean {
        return this.aiBusy;
    }

    onAiAutofillClick(): void {
        void this.runAiAutofill();
    }

    private getLinkedCompanyUuid(): string | null {
        const company = this.yukiDocument?.linkedContact ? (this.yukiDocument.linkedContact as any) : null;
        return company?.uuid || null;
    }

    private getLinkedSupplierName(): string | null {
        const company = this.yukiDocument?.linkedContact ? (this.yukiDocument.linkedContact as any) : null;
        const fromCompany = (company?.title || company?.name || '').trim();
        if (fromCompany) {
            return fromCompany;
        }

        const fromDocument = (this.yukiDocument?.contactName || '').trim();
        return fromDocument || null;
    }

    private normalizeSupplierName(value: string): string {
        return (value || '').trim().toLowerCase().replace(/\s+/g, ' ');
    }

    private getIncludeFleetStorageKeyByCompanyUuid(companyUuid: string): string {
        return `${LinkObjectToFileModalComponent.INCLUDE_FLEET_STORAGE_KEY_PREFIX_BY_COMPANY_UUID}${companyUuid}`;
    }

    private getIncludeFleetStorageKeyBySupplierName(supplierName: string): string {
        return `${LinkObjectToFileModalComponent.INCLUDE_FLEET_STORAGE_KEY_PREFIX_BY_SUPPLIER_NAME}${this.normalizeSupplierName(supplierName)}`;
    }

    private saveIncludeFleetPreferenceForCurrentSupplier(includeFleet: boolean, explicitCompanyUuid?: string | null): void {
        const companyUuid = explicitCompanyUuid || this.getLinkedCompanyUuid();
        const supplierName = this.getLinkedSupplierName();

        try {
            if (companyUuid) {
                localStorage.setItem(this.getIncludeFleetStorageKeyByCompanyUuid(companyUuid), includeFleet ? 'true' : 'false');
            }
            if (supplierName) {
                localStorage.setItem(this.getIncludeFleetStorageKeyBySupplierName(supplierName), includeFleet ? 'true' : 'false');
            }
        } catch (error) {
            this.logger.debug('Could not persist include-fleet preference', error);
        }
    }

    private readIncludeFleetPreferenceForCurrentSupplier(explicitCompanyUuid?: string | null): boolean {
        const companyUuid = explicitCompanyUuid || this.getLinkedCompanyUuid();
        const supplierName = this.getLinkedSupplierName();

        try {
            if (companyUuid) {
                const byCompany = localStorage.getItem(this.getIncludeFleetStorageKeyByCompanyUuid(companyUuid));
                if (byCompany !== null) {
                    return byCompany === 'true';
                }
            }

            if (supplierName) {
                const bySupplier = localStorage.getItem(this.getIncludeFleetStorageKeyBySupplierName(supplierName));
                if (bySupplier !== null) {
                    return bySupplier === 'true';
                }
            }

            return false;
        } catch (error) {
            this.logger.debug('Could not read include-fleet preference', error);
            return false;
        }
    }

    /** Run AI for a single linked-object row: suggest VAT/sublinks for a single project link. */
    async runAiForLine(row: LinkedObjectToFile): Promise<void> {
        if (!row) return;
        try {
            this.aiBusy = true;

            // Only operate for project links on sale invoices
            if ((row.objectType || '').toLowerCase() !== 'project') {
                this.toastr.info('Per-line AI only runs for project links.');
                return;
            }

            if (this.yukiDocument.folderId === 2) {
                // purchase invoice -> no sublinking
                this.toastr.info('Per-line AI sublinking is only available for sale invoices.');
                return;
            }

            // Resolve the file (PDF/image)
            const aiFile = await this.resolveAiFileViaApi(this.yukiDocument.uuid).catch(() => null);
            if (!aiFile) {
                this.toastr.error('Could not retrieve file for AI processing.');
                return;
            }

            // Ask AI to extract name/amount candidates from the document
            let candidates: Array<{ name: string; amount: number }> = [];
            try {
                if (aiFile.kind === 'pdf') candidates = await this.aiService.suggestSublinkCandidatesFromPdfUrl(aiFile.dataUrl);
                else candidates = await this.aiService.suggestSublinkCandidatesFromImageUrl(aiFile.dataUrl);
            } catch (e) {
                this.logger.debug('AI sublink extraction failed for line', e);
                candidates = [];
            }

            if (!candidates || candidates.length === 0) {
                // fallback: open timesheet prefill modal for this project
                const modalRef = this.modalService.open(AddChildLinkModalComponent, {
                    windowClass: 'modal-prompt modal-prompt--min-content',
                    animation: false,
                });
                // ensure latest linked objects
                await firstValueFrom(this.store.dispatch(new GetAllObjectsLinkedToFile(this.yukiDocument.uuid)));
                const latest = await firstValueFrom(this.linkedObjectsToFile$).catch(() => null);
                const projectLink = latest?.linkedObjects?.find((o: any) => o.objectId === row.objectId);
                modalRef.componentInstance.link = projectLink;
                modalRef.componentInstance.file = this.yukiDocument;
                await modalRef.result
                    .then(async () => {
                        await this.store.dispatch(new GetAllObjectsLinkedToFile(this.yukiDocument.uuid));
                        this.toastr.success('Sublinks created from timesheet prefill.');
                    })
                    .catch(() => {});
                return;
            }

            // Aggregate candidates by name
            const agg = new Map<string, number>();
            for (const c of candidates) {
                const n = (c.name || '').trim();
                if (!n) continue;
                const sum = (agg.get(n) || 0) + Number(c.amount || 0);
                agg.set(n, Number(sum.toFixed(2)));
            }

            // Map names to employees (existing linkFiles of type employee)
            const employees = (this.linkFiles || []).filter((lfx: any) => (lfx.type || '').toLowerCase() === 'employee');
            const newDtos: ChildInvoiceLinkDTO[] = [];
            for (const [name, amount] of agg.entries()) {
                const nameLower = name.toLowerCase();
                let match = employees.find((e: any) => (e.displayName || '').toLowerCase() === nameLower);
                if (!match) match = employees.find((e: any) => (e.displayName || '').toLowerCase().includes(nameLower));
                if (!match) {
                    const token = nameLower.split(' ').slice(-1)[0];
                    match = employees.find((e: any) => (e.displayName || '').toLowerCase().includes(token));
                }
                if (!match) continue;

                newDtos.push({
                    objectIdForKey: row.objectId,
                    fileIdForKey: this.yukiDocument.uuid,
                    newObjectLinkId: match.uuid,
                    amount: amount,
                    type: 'employee',
                });
            }

            if (newDtos.length === 0) {
                this.toastr.info('AI found names but none could be matched to existing employees.');
                return;
            }

            // Fetch latest linked objects to compute remaining
            await firstValueFrom(this.store.dispatch(new GetAllObjectsLinkedToFile(this.yukiDocument.uuid)));
            const linkedObjects = await firstValueFrom(this.linkedObjectsToFile$).catch(() => null);
            const projectLink = linkedObjects?.linkedObjects?.find((o: any) => o.objectId === row.objectId);
            const alreadyLinked = (projectLink?.childInvoiceLinks || []).reduce((s: number, c: any) => s + (c.amount || 0), 0);
            let remaining = +((projectLink?.price || 0) - alreadyLinked).toFixed(2) || 0;
            if (remaining <= 0) {
                this.toastr.info('No remaining amount on this project link.');
                return;
            }

            // Scale if proposed total exceeds remaining
            const proposedTotal = +newDtos.reduce((s, d) => s + (Number(d.amount) || 0), 0).toFixed(2);
            if (proposedTotal > remaining) {
                const factor = remaining / proposedTotal;
                let acc = 0;
                for (let i = 0; i < newDtos.length; i++) {
                    const raw = Number(newDtos[i].amount || 0);
                    const scaled = Math.round(raw * factor * 100) / 100;
                    newDtos[i].amount = scaled;
                    acc += scaled;
                }
                const diff = +(remaining - acc).toFixed(2);
                if (Math.abs(diff) >= 0.01 && newDtos.length > 0) {
                    newDtos[newDtos.length - 1].amount = +(Number(newDtos[newDtos.length - 1].amount || 0) + diff).toFixed(2);
                }
            }

            // Dispatch create sublinks
            await firstValueFrom(this.store.dispatch(new AddSubLinkToLinkedObjectToFile(newDtos)));
            await this.store.dispatch(new GetAllObjectsLinkedToFile(this.yukiDocument.uuid));
            this.toastr.success('Sublinks created from AI suggestions.');
        } catch (e) {
            this.logger.debug('runAiForLine failed', e);
            this.toastr.error('Per-line AI failed.');
        } finally {
            this.aiBusy = false;
        }
    }
}

export class LinkFileDTO {
    overhead?: boolean = false;
    objectId?: string;
    price?: number;
    description?: string;
    invoiceType?: string;
}
